import React, { useState, useEffect } from 'react';
import { FaExclamationCircle, FaCheckCircle, FaTasks, FaExclamationTriangle } from 'react-icons/fa'; // Example icons
import cx from 'classnames';
import { Link } from 'react-router-dom';
import {
  Row,
  Col,
  Button,
  ButtonGroup,
  Progress,
  Badge,
  Table,
  Alert,
  Spinner
} from 'reactstrap';
import {
  PieChart as RePieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip as ReTooltip,
  Legend as ReLegend,
  BarChart as ReBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';
import {  useNavigate } from 'react-router-dom';

import Widget from '../../components/Widget/Widget';
import AssessmentBarChart from '../../components/charts/charts/AssessmentBarChart';
import AssessmentCompletionChart from '../../components/charts/AssessmentCompletionChart';
import CategoryRadarChart from '../../components/charts/CategoryRadarChart';
import './Dashboard.css';
import {
  getAssessmentDashboard,
  getDetailedAssessment,
  getComplianceDashboard,
  getUsersTasksDashboard,
} from '../../services/dashboards';
import { fetchDataApi } from '../../services/riskServices';
import AssessmentDetailsChart from '../../components/charts/AssessmentDetailsChart';
import { useTranslation } from 'react-i18next';
import LoadingScreen from 'src/Loading/LoadingScreen';
import { RiProgress2Line } from 'react-icons/ri';
import { getObjectivesFromBackend, getControlMapperFromBackend } from '../../services/objectiveAIServices';
import ObjectivesPriorityChart from '../../components/charts/ObjectivesPriorityChart';
import ObjectivesEffortChart from '../../components/charts/ObjectivesEffortChart';
import ObjectivesTimelineChart from '../../components/charts/ObjectivesTimelineChart';
import ObjectivesByRegulationChart from '../../components/charts/ObjectivesByRegulationChart';
interface Post {
  id: number;
  title: string;
  updatedAt: string;
}

interface DashboardProps {
  posts: Post[];
  isFetching: boolean;
}





function Dashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [isDropdownOpened, setIsDropdownOpened] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [assessmentDashboard, setAssessmentDashboard] = useState<any | null>(null);
  const [detailedAssessment, setDetailedAssessment] = useState<any | null>(null);
  const [isAssessmentLoading, setIsAssessmentLoading] = useState(true);
  const [objectivesData, setObjectivesData] = useState<any[] | null>(null);
  const [isObjectivesLoading, setIsObjectivesLoading] = useState(true);
  const [controlMapperData, setControlMapperData] = useState<any | null>(null);
  const [isControlMapperLoading, setIsControlMapperLoading] = useState(true);
  const [complianceOverviewLoading, setComplianceOverviewLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [usersTasksDashboard, setUsersTasksDashboard] = useState<any[]>([]);
  const [risksByCategory, setRisksByCategory] = useState<{ name: string; count: number }[]>([]);
  const [risksByLevel, setRisksByLevel] = useState<{ name: string; value: number }[]>([]);

  const fetchAssessmentDashboard = async () => {
    setIsAssessmentLoading(true);
    try {
      const res = await getAssessmentDashboard();
      if (res.success) {
        setAssessmentDashboard(res.data);
      } else {
        setAssessmentDashboard(null);
      }
    } catch (error) {
      console.error('Error fetching assessment dashboard:', error);
      setAssessmentDashboard(null);
    } finally {
      setIsAssessmentLoading(false);
    }
  };

  const fetchDetailedAssessment = async () => {
    try {
      const res = await getDetailedAssessment();
      if (res.success) {
        setDetailedAssessment(res.data);
      } else {
        setDetailedAssessment(null);
      }
    } catch (error) {
      console.error('Error fetching detailed assessment:', error);
      setDetailedAssessment(null);
    }
  };

  const fetchObjectives = async () => {
    setIsObjectivesLoading(true);
    try {
      const objectives = await getObjectivesFromBackend();
      if (objectives && Array.isArray(objectives) && objectives.length > 0) {
        // Extract roadmap from the first item
        const roadmap = objectives[0]?.roadmap || [];
        setObjectivesData(roadmap);
      } else {
        setObjectivesData(null);
      }
    } catch (error) {
      console.error('Error fetching objectives:', error);
      setObjectivesData(null);
    } finally {
      setIsObjectivesLoading(false);
    }
  };

  const fetchControlMapper = async () => {
    setIsControlMapperLoading(true);
    try {
      const data = await getControlMapperFromBackend();
      if (
        data &&
        (
          (data.individual && Array.isArray(data.individual) && data.individual.length > 0) ||
          (data.grouped && Array.isArray(data.grouped) && data.grouped.length > 0)
        )
      ) {
        setControlMapperData(data);
      } else {
        setControlMapperData(null);
      }
    } catch {
      setControlMapperData(null);
    } finally {
      setIsControlMapperLoading(false);
    }
  };

  useEffect(() => {
    fetchAssessmentDashboard();
    fetchDetailedAssessment();
    fetchObjectives();
    fetchControlMapper();
  }, []);

  useEffect(() => {
    const loadComplianceAndRisks = async () => {
      setComplianceOverviewLoading(true);
      try {
        const [complianceRes, tasksRes, risksRes] = await Promise.all([
          getComplianceDashboard().catch(() => ({ success: false, data: null })),
          getUsersTasksDashboard().catch(() => ({ success: false, data: [] })),
          fetchDataApi(`cbe-risks?perPage=500&pageNo=0`).catch(() => ({ data: [] })),
        ]);

        if (complianceRes.success && complianceRes.data) {
          setDashboardData(complianceRes.data);
        } else {
          setDashboardData(null);
        }

        const taskRows =
          tasksRes?.success && Array.isArray(tasksRes.data)
            ? tasksRes.data
            : Array.isArray((tasksRes as any)?.data)
              ? (tasksRes as any).data
              : Array.isArray(tasksRes)
                ? (tasksRes as unknown as any[])
                : [];
        setUsersTasksDashboard(taskRows);

        const risks = Array.isArray(risksRes?.data) ? risksRes.data : [];
        const byCat: Record<string, number> = {};
        const byLevel: Record<string, number> = {};
        risks.forEach((r: any) => {
          const cat = r?.category?.name || 'Unknown';
          byCat[cat] = (byCat[cat] || 0) + 1;
          const lv = String(r?.risk ?? 'Unknown').trim() || 'Unknown';
          byLevel[lv] = (byLevel[lv] || 0) + 1;
        });
        setRisksByCategory(
          Object.entries(byCat)
            .map(([name, count]) => ({ name, count }))
            .sort((a, b) => b.count - a.count)
            .slice(0, 12),
        );
        setRisksByLevel(
          Object.entries(byLevel).map(([name, value]) => ({ name, value })),
        );
      } catch (e) {
        console.error('Dashboard compliance/risk overview fetch failed', e);
        setDashboardData(null);
        setUsersTasksDashboard([]);
        setRisksByCategory([]);
        setRisksByLevel([]);
      } finally {
        setComplianceOverviewLoading(false);
      }
    };
    loadComplianceAndRisks();
  }, []);

  const controlMapperSummary = React.useMemo(() => {
    if (!controlMapperData) {
      return {
        totalIndividual: 0,
        totalGroupedSets: 0,
        totalGroupedControls: 0,
        totalControls: 0,
        ownerChartData: [] as { name: string; value: number }[],
      };
    }

    const totalIndividual = Array.isArray(controlMapperData.individual)
      ? controlMapperData.individual.length
      : 0;

    const totalGroupedSets = Array.isArray(controlMapperData.grouped)
      ? controlMapperData.grouped.length
      : 0;

    const totalGroupedControls = Array.isArray(controlMapperData.grouped)
      ? controlMapperData.grouped.reduce(
          (sum: number, group: any) =>
            sum + (Array.isArray(group.mapped_controls) ? group.mapped_controls.length : 0),
          0,
        )
      : 0;

    const totalControls = totalIndividual + totalGroupedControls;

    const ownerCounts: Record<string, number> = {};

    if (Array.isArray(controlMapperData.individual)) {
      controlMapperData.individual.forEach((item: any) => {
        const ownerName =
          item?.suggested_owner_role_department?.name || 'Unassigned';
        ownerCounts[ownerName] = (ownerCounts[ownerName] || 0) + 1;
      });
    }

    if (Array.isArray(controlMapperData.grouped)) {
      controlMapperData.grouped.forEach((group: any) => {
        const ownerName =
          group?.suggested_owner_role_department?.name || 'Unassigned';
        const groupCount = Array.isArray(group.mapped_controls)
          ? group.mapped_controls.length
          : 0;
        ownerCounts[ownerName] = (ownerCounts[ownerName] || 0) + groupCount;
      });
    }

    const ownerChartData = Object.entries(ownerCounts)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5);

    return {
      totalIndividual,
      totalGroupedSets,
      totalGroupedControls,
      totalControls,
      ownerChartData,
    };
  }, [controlMapperData]);

  const controlMapperDistributionData = [
    { name: t('Dashboard.individualControls'), value: controlMapperSummary.totalIndividual },
    { name: t('Dashboard.groupedControls'), value: controlMapperSummary.totalGroupedControls },
  ].filter(item => item.value > 0);

  const CONTROL_MAPPER_COLORS = ['#3b82f6', '#10b981', '#f97316', '#6366f1', '#ef4444'];

  const controlDetailsData = React.useMemo(() => {
    if (!controlMapperData) return [];

    const details: {
      id: string;
      code: string;
      name: string;
      description: string;
      objective: string;
      owner: string;
      type: string;
      source: string;
      groupName?: string;
    }[] = [];

    if (Array.isArray(controlMapperData.individual)) {
      controlMapperData.individual.forEach((item: any) => {
        if (!item || !item.control) return;
        details.push({
          id: item.control.id,
          code: item.control.code || '',
          name: item.control.name || '',
          description: item.control.description || '',
          objective: item.control.objective || '',
          owner: item?.suggested_owner_role_department?.name || t('Dashboard.unassigned'),
          type: item.control.mandatoryType || t('Dashboard.na'),
          source: 'Individual',
        });
      });
    }

    if (Array.isArray(controlMapperData.grouped)) {
      controlMapperData.grouped.forEach((group: any) => {
        if (!Array.isArray(group.mapped_controls)) return;
        group.mapped_controls.forEach((control: any) => {
          if (!control) return;
          details.push({
            id: control.id,
            code: control.code || '',
            name: control.name || '',
            description: control.description || '',
            objective: control.objective || '',
            owner: group?.suggested_owner_role_department?.name || 'Unassigned',
            type: control.mandatoryType || 'N/A',
            source: 'Grouped',
            groupName: group?.name || '',
          });
        });
      });
    }

    return details;
  }, [controlMapperData]);

  const formatDate = (str: string) => {
    return str.replace(/,.*$/, "");
  };

  const toggleDropdown = () => {
    setIsDropdownOpened(!isDropdownOpened);
  };

  if (isLoading) {
    return (
      <div className="loading-screen" 
      style={{ 
        backgroundColor: 'rgba(255, 255, 255, 0.8)'}}>
                     <div><LoadingScreen/></div>
                   </div>
    );
  }


  return (
   <>
    <div className=" dashboard">
    <div className="container-fluid p-3 controls-catalog-main" 
    style={{ paddingTop: '1.25rem', zoom: 0.9 }}>
     
        <>
       <div>
      <Row className="mb-3 g-3">
        <Col sm={12} md={3}>
          <div className="card bg-white h-100 p-3 shadow-sm">
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <h6 className="fw-semi-bold text-gray-600">{t('Dashboard.Remaining_Tasks')}</h6>
                <h2 className="mt-2">
                  {complianceOverviewLoading
                    ? '—'
                    : (dashboardData?.in_progress ?? 0) + (dashboardData?.not_started ?? 0)}
                </h2>
              </div>
              <div className="bg-primary p-2 rounded">
                <FaExclamationCircle size={20} className="text-white" />
              </div>
            </div>
          </div>
        </Col>
        <Col sm={12} md={3}>
          <div className="card bg-white h-100 p-3 shadow-sm">
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <h6 className="fw-semi-bold text-gray-600">{t('Dashboard.Completed_Tasks')}</h6>
                <h2 className="mt-2">{complianceOverviewLoading ? '—' : dashboardData?.done ?? '—'}</h2>
              </div>
              <div className="bg-success p-2 rounded">
                <FaCheckCircle size={20} className="text-white" />
              </div>
            </div>
          </div>
        </Col>
        <Col sm={12} md={3}>
          <div className="card bg-white h-100 p-3 shadow-sm">
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <h6 className="fw-semi-bold text-gray-600">{t('Dashboard.Tasks_in_progress')}</h6>
                <h2 className="mt-2">{complianceOverviewLoading ? '—' : dashboardData?.in_progress ?? '—'}</h2>
              </div>
              <div className="bg-warning p-2 rounded">
                <RiProgress2Line size={20} className="text-white" />
              </div>
            </div>
          </div>
        </Col>
        <Col sm={12} md={3}>
          <div className="card bg-white h-100 p-3 shadow-sm">
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <h6 className="fw-semi-bold text-gray-600">{t('Dashboard.compliance', 'Compliance %')}</h6>
                <h2 className="mt-2">
                  {complianceOverviewLoading || !dashboardData
                    ? '—'
                    : `${dashboardData.compliance ?? 0}%`}
                </h2>
              </div>
              <div className="bg-info p-2 rounded">
                <FaCheckCircle size={20} className="text-white" />
              </div>
            </div>
          </div>
        </Col>
      </Row>

      {(risksByCategory.length > 0 || risksByLevel.length > 0) && (
        <Row className="mt-3 mb-3">
          {risksByCategory.length > 0 && (
            <Col xs={12} md={6}>
              <Widget className="custom-widget">
                <h5 className="widget-title">
                  <span className="fw-semi-bold">{t('Dashboard.risksPerCategory', 'Risks per category')}</span>
                </h5>
                <hr className="widget-separator" />
                <ResponsiveContainer height={280} width="100%">
                  <ReBarChart
                    data={risksByCategory.map((d: { name: string; count: number }) => ({
                      name: d.name,
                      count: d.count,
                    }))}
                    margin={{ top: 12, right: 12, left: 0, bottom: 48 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" angle={-30} textAnchor="end" interval={0} height={70} tick={{ fontSize: 11 }} />
                    <YAxis allowDecimals={false} />
                    <ReTooltip />
                    <Bar
                      dataKey="count"
                      fill="#f97316"
                      name={String(t('Risk.Risks', { defaultValue: 'Risks' }))}
                      radius={[4, 4, 0, 0]}
                    />
                  </ReBarChart>
                </ResponsiveContainer>
              </Widget>
            </Col>
          )}
          {risksByLevel.length > 0 && (
            <Col xs={12} md={6}>
              <Widget className="custom-widget">
                <h5 className="widget-title">
                  <span className="fw-semi-bold">{t('Dashboard.risksByLevel', 'Risks by level')}</span>
                </h5>
                <hr className="widget-separator" />
                <ResponsiveContainer height={280} width="100%">
                  <RePieChart>
                    <Pie
                      data={risksByLevel}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={90}
                      paddingAngle={2}
                      dataKey="value"
                      nameKey="name"
                    >
                      {risksByLevel.map((entry: { name: string; value: number }, index: number) => (
                        <Cell
                          key={entry.name}
                          fill={['#22c55e', '#eab308', '#f97316', '#ef4444', '#64748b'][index % 5]}
                        />
                      ))}
                    </Pie>
                    <ReTooltip />
                    <ReLegend />
                  </RePieChart>
                </ResponsiveContainer>
              </Widget>
            </Col>
          )}
        </Row>
      )}

      {usersTasksDashboard.length > 0 && (
        <Row className="mt-3 mb-3">
          <Col xs={12}>
            <div className="bg-white p-3 rounded shadow-sm w-100">
              <h5 className="mt-0 mb-3">
                <i className="fa fa-user mr-xs opacity-70" /> {t('Dashboard.Users_Tasks')}
              </h5>
              <div className="table-responsive w-100">
                <table className="styled-table rounded w-100">
                  <thead style={{ backgroundColor: '#cccbca' }}>
                    <tr>
                      <th>{t('Dashboard.Name')}</th>
                      <th>{t('Dashboard.Username')}</th>
                      <th>{t('Dashboard.Email')}</th>
                      <th>{t('Dashboard.Not_Completed')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usersTasksDashboard.map((user: any) => (
                      <tr key={user.id ?? user.email}>
                        <td>{user.name}</td>
                        <td>{user.username}</td>
                        <td>{user.email}</td>
                        <td>
                          <Badge
                            color={
                              user.inProgressTasks > 5 ? 'danger' : user.inProgressTasks > 3 ? 'warning' : 'success'
                            }
                            className="ml-xs"
                          >
                            {user.inProgressTasks}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </Col>
        </Row>
      )}

          {/* Objectives Charts Section */}
          {!isObjectivesLoading && objectivesData && objectivesData.length > 0 && (
            <>
              <Row className="mt-3">
                <Col xs={12}>
                  <h4 className="mb-3 px-3">
                    <i className="fa fa-bullseye mr-xs opacity-70" /> {t('Dashboard.objectivesOverview')}
                  </h4>
                </Col>
              </Row>
              <Row className="mt-2">
                <Col sm={12} md={3} className="rounded">
                  <div className='card bg-white h-100 p-3 shadow-sm'>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: "space-between" }}>
                      <div>
                        <h6 className="fw-semi-bold text-gray-600">{t('Dashboard.totalObjectives')}</h6>
                        <h2 className="mt-2">{objectivesData.length}</h2>
                      </div>
                      <div className='bg-primary p-2 rounded'>
                        <FaTasks size={20} className="text-white" />
                      </div>
                    </div>
                  </div>
                </Col>
                <Col sm={12} md={3} className="rounded">
                  <div className='card bg-white h-100 p-3 shadow-sm'>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: "space-between" }}>
                      <div>
                        <h6 className="fw-semi-bold text-gray-600">{t('Dashboard.highPriority')}</h6>
                        <h2 className="mt-2">{objectivesData.filter((obj: any) => obj.priority_rank <= 2).length}</h2>
                      </div>
                      <div className='bg-danger p-2 rounded'>
                        <FaExclamationCircle size={20} className="text-white" />
                      </div>
                    </div>
                  </div>
                </Col>
                <Col sm={12} md={3} className="rounded">
                  <div className='card bg-white h-100 p-3 shadow-sm'>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: "space-between" }}>
                      <div>
                        <h6 className="fw-semi-bold text-gray-600">{t('Dashboard.highEffort')}</h6>
                        <h2 className="mt-2">{objectivesData.filter((obj: any) => obj.estimated_effort === 'High').length}</h2>
                      </div>
                      <div className='bg-warning p-2 rounded'>
                        <FaExclamationTriangle size={20} className="text-white" />
                      </div>
                    </div>
                  </div>
                </Col>
                <Col sm={12} md={3} className="rounded">
                  <div className='card bg-white h-100 p-3 shadow-sm'>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: "space-between" }}>
                      <div>
                        <h6 className="fw-semi-bold text-gray-600">{t('Dashboard.regulatoryFrameworks')}</h6>
                        <h2 className="mt-2">
                          {new Set(objectivesData.map((obj: any) => obj.regulatory_reference)).size}
                        </h2>
                      </div>
                      <div className='bg-info p-2 rounded'>
                        <FaCheckCircle size={20} className="text-white" />
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>
              <Row className="mt-3">
                <Col xs={12} md={6}>
                  <Widget className="custom-widget">
                    <h5 className="widget-title">
                      <span className="fw-semi-bold">{t('Dashboard.objectivesByPriority')}</span>
                    </h5>
                    <hr className="widget-separator" />
                    <ObjectivesPriorityChart data={objectivesData} />
                  </Widget>
                </Col>
                <Col xs={12} md={6}>
                  <Widget className="custom-widget">
                    <h5 className="widget-title">
                      <span className="fw-semi-bold">{t('Dashboard.objectivesByEffort')}</span>
                    </h5>
                    <hr className="widget-separator" />
                    <ObjectivesEffortChart data={objectivesData} />
                  </Widget>
                </Col>
              </Row>
              <Row className="mt-3">
                <Col xs={12} md={6}>
                  <Widget className="custom-widget">
                    <h5 className="widget-title">
                      <span className="fw-semi-bold">{t('Dashboard.objectivesTimeline')}</span>
                    </h5>
                    <hr className="widget-separator" />
                    <ObjectivesTimelineChart data={objectivesData} />
                  </Widget>
                </Col>
                <Col xs={12} md={6}>
                  <Widget className="custom-widget">
                    <h5 className="widget-title">
                      <span className="fw-semi-bold">{t('Dashboard.objectivesByRegulation')}</span>
                    </h5>
                    <hr className="widget-separator" />
                    <ObjectivesByRegulationChart data={objectivesData} />
                  </Widget>
                </Col>
              </Row>
            </>
          )}

          {/* Call-to-action when no objectives exist */}
          {!isObjectivesLoading && (!objectivesData || objectivesData.length === 0) && (
            <Row className="mt-3">
              <Col xs={12}>
                <div className="card bg-white p-4 shadow-sm d-flex flex-column flex-md-row align-items-center justify-content-between">
                  <div className="mb-3 mb-md-0">
                    <h5 className="fw-semi-bold text-gray-800 mb-1">
                      {t('Dashboard.noObjectivesFound')}
                    </h5>
                    <p className="text-muted mb-0">
                      {t('Dashboard.generateObjectivesHint')}
                    </p>
                  </div>
                  <Button
                    color="primary"
                    className="mt-2 mt-md-0"
                    onClick={() => navigate('/objective')}
                  >
                    {t('Dashboard.goToObjectives')}
                  </Button>
                </div>
              </Col>
            </Row>
          )}

        {/* Control Mapper Charts Section */}
        {controlMapperData && controlMapperSummary.totalControls > 0 && (
          <>
            <Row className="mt-4">
              <Col xs={12}>
                <h4 className="mb-3 px-3">
                  <i className="fa fa-shield mr-xs opacity-70" /> Control Mapper Overview
                </h4>
              </Col>
            </Row>
            <Row className="mt-2">
              <Col xs={12} md={6}>
                <Widget className="custom-widget">
                  <h5 className="widget-title">
                    <span className="fw-semi-bold">{t('Dashboard.individualVsGrouped')}</span>
                  </h5>
                  <hr className="widget-separator" />
                  {isControlMapperLoading ? (
                    <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: 300 }}>
                      <Spinner color="primary" style={{ width: '3rem', height: '3rem' }} />
                      <div className="mt-3 text-muted">{t('Dashboard.loadingControlMapper')}</div>
                    </div>
                  ) : controlMapperDistributionData.length > 0 ? (
                    <ResponsiveContainer height={300} width="100%">
                      <RePieChart>
                        <Pie
                          data={controlMapperDistributionData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          outerRadius={90}
                          innerRadius={40}
                          dataKey="value"
                        >
                          {controlMapperDistributionData.map((entry, index) => (
                            <Cell
                              key={entry.name}
                              fill={CONTROL_MAPPER_COLORS[index % CONTROL_MAPPER_COLORS.length]}
                            />
                          ))}
                        </Pie>
                        <ReTooltip />
                        <ReLegend layout="horizontal" align="center" verticalAlign="bottom" />
                      </RePieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="d-flex align-items-center justify-content-center" style={{ minHeight: 260 }}>
                      <span className="text-muted">{t('Dashboard.noControlMapperData')}</span>
                    </div>
                  )}
                </Widget>
              </Col>
              <Col xs={12} md={6}>
                <Widget className="custom-widget">
                  <h5 className="widget-title">
                    <span className="fw-semi-bold">Top Owners by Number of Controls</span>
                  </h5>
                  <hr className="widget-separator" />
                  {isControlMapperLoading ? (
                    <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: 300 }}>
                      <Spinner color="primary" style={{ width: '3rem', height: '3rem' }} />
                      <div className="mt-3 text-muted">{t('Dashboard.loadingControlMapper')}</div>
                    </div>
                  ) : controlMapperSummary.ownerChartData.length > 0 ? (
                    <ResponsiveContainer height={300} width="100%">
                      <ReBarChart
                        data={controlMapperSummary.ownerChartData}
                        margin={{ top: 20, right: 20, left: 0, bottom: 40 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" angle={-30} textAnchor="end" interval={0} height={60} />
                        <YAxis />
                        <ReTooltip />
                        <Bar dataKey="value" fill="#3b82f6" name={String(t('Dashboard.controls'))} />
                      </ReBarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="d-flex align-items-center justify-content-center" style={{ minHeight: 260 }}>
                      <span className="text-muted">{t('Dashboard.noOwnerData')}</span>
                    </div>
                  )}
                </Widget>
              </Col>
            </Row>
            <Row className="mt-3">
              <Col xs={12}>
                <Widget className="custom-widget">
                  <h5 className="widget-title">
                    <span className="fw-semi-bold">{t('Dashboard.mappedControlsDetail')}</span>
                  </h5>
                  <hr className="widget-separator" />
                  {isControlMapperLoading ? (
                    <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: 300 }}>
                      <Spinner color="primary" style={{ width: '3rem', height: '3rem' }} />
                      <div className="mt-3 text-muted">{t('Dashboard.loadingControlMapper')}</div>
                    </div>
                  ) : controlDetailsData.length > 0 ? (
                    <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                      <table className="table table-striped table-hover mb-0">
                        <thead>
                          <tr>
                            <th style={{ minWidth: 90 }}>Code</th>
                            <th style={{ minWidth: 200 }}>Control Name</th>
                            <th style={{ minWidth: 260 }}>Description</th>
                            <th style={{ minWidth: 140 }}>Objective</th>
                            <th style={{ minWidth: 160 }}>Owner Department</th>
                            <th style={{ minWidth: 120 }}>Mandatory Type</th>
                            <th style={{ minWidth: 100 }}>Source</th>
                            <th style={{ minWidth: 200 }}>Group Name</th>
                          </tr>
                        </thead>
                        <tbody>
                          {controlDetailsData.map((control) => (
                            <tr key={control.id}>
                              <td>{control.code || '-'}</td>
                              <td>{control.name || '-'}</td>
                              <td>{control.description || '-'}</td>
                              <td>{control.objective || '-'}</td>
                              <td>{control.owner === 'Unassigned' ? t('Dashboard.unassigned') : control.owner}</td>
                              <td>{control.type === 'N/A' ? t('Dashboard.na') : control.type}</td>
                              <td>{control.source === 'Individual' ? t('Dashboard.individualControls') : t('Dashboard.groupedControls')}</td>
                              <td>{control.groupName || (control.source === 'Grouped' ? '—' : t('Dashboard.na'))}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="d-flex align-items-center justify-content-center" style={{ minHeight: 200 }}>
                      <span className="text-muted">{t('Dashboard.noMappedControls')}</span>
                    </div>
                  )}
                </Widget>
              </Col>
            </Row>
          </>
        )}

<Row className="mt-3">
  <Col xs={12} md={4}>
    <Widget className="custom-widget">
      <h5 className="widget-title">
        <span className="fw-semi-bold">{t('Dashboard.assessmentOverallScore')}</span>
      </h5>
      <hr className="widget-separator" />
      {isAssessmentLoading ? (
        <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: 300 }}>
          <Spinner color="primary" style={{ width: '3rem', height: '3rem' }} />
          <div className="mt-3 text-muted">{t('Dashboard.loadingAssessment')}</div>
        </div>
      ) : assessmentDashboard ? (
        <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: 300 }}>
          <div
            style={{
              width: 150,
              height: 150,
              borderRadius: '50%',
              border: '10px solid #1f7c50',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            }}
          >
            <div className="text-center">
              <div style={{ fontSize: 32, fontWeight: 700 }}>
                {assessmentDashboard.overallScore}%
              </div>
              <div style={{ fontSize: 13, color: '#64748b' }}>{t('Dashboard.overallScore')}</div>
            </div>
          </div>
          <div className="mt-3 text-center" style={{ fontSize: 14 }}>
            <div className="mb-2">
              <strong>{t('Dashboard.totalScore')}</strong> {assessmentDashboard.actualScore} / {assessmentDashboard.maxPossibleScore}
            </div>
            <div className="mb-2 text-muted">
              <strong>{t('Dashboard.questionsAnswered')}</strong> {assessmentDashboard.answeredQuestions} / {assessmentDashboard.totalQuestions}
            </div>
            <div className="text-muted" style={{ fontSize: 12 }}>
              {assessmentDashboard.totalQuestions > 0 
                ? `${Math.round((assessmentDashboard.answeredQuestions / assessmentDashboard.totalQuestions) * 100)}% ${t('Dashboard.completed')}`
                : t('Dashboard.noQuestionsAvailable')}
            </div>
          </div>
        </div>
      ) : (
        <div className="d-flex align-items-center justify-content-center" style={{ minHeight: 260 }}>
          <span className="text-muted">{t('Dashboard.noAssessmentData')}</span>
        </div>
      )}
    </Widget>
  </Col>

  <Col xs={12} md={8}>
    <Widget className="custom-widget">
      <h5 className="widget-title">
        <span className="fw-semi-bold">{t('Dashboard.assessmentScoreByCategory')}</span>
      </h5>
      <hr className="widget-separator" />
      {isAssessmentLoading ? (
        <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: 300 }}>
          <Spinner color="primary" style={{ width: '3rem', height: '3rem' }} />
          <div className="mt-3 text-muted">{t('Dashboard.loadingCategoryScores')}</div>
        </div>
      ) : assessmentDashboard && assessmentDashboard.categories && assessmentDashboard.categories.length > 0 ? (
        <div>
          <AssessmentBarChart
            data={assessmentDashboard.categories.map((c: any) => ({
              categoryName: c.categoryName,
              score: c.score,
            }))}
          />
          <div className="mt-3 px-3" style={{ fontSize: 12 }}>
            <div className="row">
              {assessmentDashboard.categories.map((c: any, idx: number) => (
                <div key={idx} className="col-6 mb-2">
                  <strong>{c.categoryName}:</strong> {c.answeredQuestions} / {c.totalQuestions} answered ({c.score}%)
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="d-flex align-items-center justify-content-center" style={{ minHeight: 260 }}>
          <span className="text-muted">{t('Dashboard.noCategoryScoreData')}</span>
        </div>
      )}
    </Widget>
  </Col>
</Row>

<Row className="mt-3">
  <Col xs={12} md={4}>
    <Widget className="custom-widget">
      <h5 className="widget-title">
        <span className="fw-semi-bold">{t('Dashboard.completionStatus')}</span>
      </h5>
      <hr className="widget-separator" />
      {isAssessmentLoading ? (
        <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: 300 }}>
          <Spinner color="primary" style={{ width: '3rem', height: '3rem' }} />
          <div className="mt-3 text-muted">{t('Dashboard.loadingCompletion')}</div>
        </div>
      ) : assessmentDashboard ? (
        <AssessmentCompletionChart data={assessmentDashboard} />
      ) : (
        <div className="d-flex align-items-center justify-content-center" style={{ minHeight: 260 }}>
          <span className="text-muted">{t('Dashboard.noCompletionData')}</span>
        </div>
      )}
    </Widget>
  </Col>

  <Col xs={12} md={8}>
    <Widget className="custom-widget">
      <h5 className="widget-title">
        <span className="fw-semi-bold">{t('Dashboard.categoryPerformanceComparison')}</span>
      </h5>
      <hr className="widget-separator" />
      {isAssessmentLoading ? (
        <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: 400 }}>
          <Spinner color="primary" style={{ width: '3rem', height: '3rem' }} />
          <div className="mt-3 text-muted">{t('Dashboard.loadingCategoryComparison')}</div>
        </div>
      ) : assessmentDashboard && assessmentDashboard.categories && assessmentDashboard.categories.length > 0 ? (
        <CategoryRadarChart data={assessmentDashboard} />
      ) : (
        <div className="d-flex align-items-center justify-content-center" style={{ minHeight: 400 }}>
          <span className="text-muted">{t('Dashboard.noCategoryData')}</span>
        </div>
      )}
    </Widget>
  </Col>
</Row>

       </div>

  
        

     </>
  </div>

 </div>
 </>
  );
}

export default Dashboard;
